import React, { useEffect } from 'react';

import { FlowContextProvider, FlowValues, StepWrapper, useMutation, useReturnToPath } from '@betterment/js-runtime';
import { Helmet } from 'react-helmet';
import { Route, Routes } from 'react-router-dom';

import Confirm from './Confirm';
import Success from './Success';

import BlockedError from '../Checking/components/BlockedError';
import { useCustomer } from '../Checking/components/CustomerProvider';
import { useUser } from '../Checking/contexts/UserProvider';
import { Account, AccountCreationRequestResponse } from '../Checking/data';
import { CloseButton, ModalContent, ModalOverlay } from '../Checking/modal-helpers';
import { isLocked } from '../Checking/models/card';
import { isManualReview, spendingAccountName } from '../Checking/models/customer';
import FullPageLoader from '../Shared/components/FullPageLoader';
import { locationAssign } from '../Shared/path-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

export type FlowState = FlowValues<{
  requestId: string;
  defaultAccountName: string;
  accountId?: Account['id'];
}>;

const CheckingAccountCreationController: React.FC = () => {
  const returnToPath = useReturnToPath(urlBuilder.getAppChecking.absolute());
  const handleClose = () => locationAssign(returnToPath);

  const { customer } = useCustomer();
  const { user } = useUser();

  const [issueAccountCreationRequest, { loading, data: creationRequest }] = useMutation<
    AccountCreationRequestResponse,
    {}
  >(urlBuilder.apiV2CheckingAccountCreationRequests.absolute());

  const isCardLocked = customer && isLocked(customer.card);
  const isCustomerLocked = customer.isLocked;
  const isCustomerInManualReview = customer && isManualReview(customer);
  const isAccountGroupLocked = user?.checkingEligibilityStatus === 'account_group_locked';
  const isUserInManualReview = user?.checkingEligibilityStatus === 'manual_review';
  const isSuspended =
    user?.checkingEligibilityStatus === 'suspended' ||
    user?.checkingEligibilityStatus === 'suspended_documents_required';
  const isInBlockedState =
    isCardLocked ||
    isCustomerLocked ||
    isCustomerInManualReview ||
    isAccountGroupLocked ||
    isSuspended ||
    isUserInManualReview;

  useEffect(() => {
    if (!isInBlockedState) {
      void issueAccountCreationRequest();
    }
  }, [isInBlockedState, issueAccountCreationRequest]);

  if (loading || (!isInBlockedState && creationRequest === null)) {
    return <FullPageLoader />;
  }

  return (
    <ModalOverlay>
      <ModalContent aria-label="Checking signup">
        <CloseButton closeHandler={handleClose} />
        <Helmet>
          <title>Checking signup</title>
        </Helmet>
        <FlowContextProvider<FlowState>
          defaultStep={isInBlockedState ? 'locked_error' : undefined}
          initialValues={creationRequest || undefined}
          basePath="/checking/account_creation"
          routePath=""
          returnToPath={returnToPath}
        >
          <Routes>
            <Route
              data-feature="checking_account_management"
              path="/confirm"
              element={
                <StepWrapper<FlowState> name="confirm">
                  {stepProps => <Confirm {...stepProps} {...creationRequest} />}
                </StepWrapper>
              }
            />
            <Route
              data-feature="checking_account_management"
              path="/success"
              element={
                <StepWrapper<FlowState> name="success">
                  {stepProps => <Success {...stepProps} spendingAccountName={spendingAccountName(customer)!} />}
                </StepWrapper>
              }
            />
            <Route
              data-feature="checking_account_management"
              path="/locked_error"
              element={
                <StepWrapper name="locked_error">
                  <BlockedError
                    title="Checking signup"
                    customer={customer}
                    accountId={customer.card.spendingAccountId}
                    user={user!}
                  />
                </StepWrapper>
              }
            />
          </Routes>
        </FlowContextProvider>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CheckingAccountCreationController;
